import { useRef, useState } from "react";
import { Upload, Trash2, ArrowUp, ArrowDown, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, EQUIP_CATEGORIES, PHOTO_SEASONS, photoSeason } from "@/lib/api";

export function ImageInput({ value, onChange, folder = "chalet", testId }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("folder", folder);
    setBusy(true);
    try { const r = await api.post("/admin/upload", fd); onChange(r.data.url, r.data.public_id); toast.success("Image téléversée"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-3 items-start">
      {value && <img src={value} alt="" className="w-20 h-20 object-cover rounded-lg border border-sand shrink-0" />}
      <div className="flex-1 space-y-2">
        <input className="input" placeholder="https://… (URL de l'image)" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        <div className="flex gap-2"><button type="button" onClick={() => ref.current.click()} disabled={busy} className="btn-ghost text-xs py-2 min-h-0"><Upload size={14} /> {busy ? "Envoi…" : "Téléverser (Cloudinary)"}</button>{value && <button type="button" onClick={() => onChange("")} className="btn-ghost text-xs py-2 min-h-0 text-red-700 border-red-200"><Trash2 size={14} /> Retirer</button>}</div>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </div>
    </div>
  );
}

function ListControls({ i, len, onMove, onRemove }) {
  return (
    <div className="flex flex-col gap-1 shrink-0">
      <button type="button" onClick={() => onMove(i, -1)} disabled={i === 0} className="w-8 h-8 rounded-lg border border-sand flex items-center justify-center disabled:opacity-30" aria-label="Monter"><ArrowUp size={14} /></button>
      <button type="button" onClick={() => onMove(i, 1)} disabled={i === len - 1} className="w-8 h-8 rounded-lg border border-sand flex items-center justify-center disabled:opacity-30" aria-label="Descendre"><ArrowDown size={14} /></button>
      <button type="button" onClick={() => onRemove(i)} className="w-8 h-8 rounded-lg border border-red-200 text-red-700 flex items-center justify-center" aria-label="Supprimer"><Trash2 size={14} /></button>
    </div>
  );
}

const useList = (value, onChange) => {
  const list = Array.isArray(value) ? value : [];
  const move = (i, d) => { const l = [...list]; const j = i + d; if (j < 0 || j >= l.length) return; [l[i], l[j]] = [l[j], l[i]]; onChange(l); };
  const remove = (i) => onChange(list.filter((_, k) => k !== i));
  const update = (i, patch) => onChange(list.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  return { list, move, remove, update };
};

export function ImagesField({ value, onChange, mainImage, onMain, folder }) {
  const { list, move, remove, update } = useList(value, onChange);
  return (
    <div className="space-y-3">
      {list.map((im, i) => (
        <div key={i} className="flex gap-3 items-start bg-cream/60 rounded-xl p-3">
          {im.url && <img src={im.url} alt="" className="w-20 h-20 object-cover rounded-lg shrink-0" />}
          <div className="flex-1 space-y-2">
            <ImageInput value={im.url} onChange={(url, public_id) => update(i, { url, public_id })} folder={folder} />
            <input className="input" placeholder="Texte ALT (description de la photo)" value={im.alt || ""} onChange={(e) => update(i, { alt: e.target.value })} />
            <label className="flex items-center gap-2 text-xs text-charcoal/70">Saison
              <select className="input py-1.5 min-h-0 w-auto text-xs" value={photoSeason(im)} onChange={(e) => update(i, { season: e.target.value })} data-testid={`image-season-${i}`}>
                {PHOTO_SEASONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span className="text-charcoal/40 hidden sm:inline">Hiver : sept.→mars · Été : avr.→août</span>
            </label>
            {onMain && <button type="button" onClick={() => onMain(im.url)} className={`text-xs inline-flex items-center gap-1 ${mainImage === im.url ? "text-gold font-semibold" : "text-charcoal/60 hover:text-forest"}`}><Star size={12} className={mainImage === im.url ? "fill-gold" : ""} /> {mainImage === im.url ? "Photo principale" : "Définir comme photo principale"}</button>}
          </div>
          <ListControls i={i} len={list.length} onMove={move} onRemove={remove} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, { url: "", alt: "", season: "toutes" }])} className="btn-ghost text-xs py-2 min-h-0"><Plus size={14} /> Ajouter une photo</button>
    </div>
  );
}

export function RepeaterField({ value, onChange, fields, addLabel = "Ajouter" }) {
  const { list, move, remove, update } = useList(value, onChange);
  return (
    <div className="space-y-3">
      {list.map((row, i) => (
        <div key={i} className="flex gap-3 items-start bg-cream/60 rounded-xl p-3">
          <div className="flex-1 grid sm:grid-cols-2 gap-2">
            {fields.map((f) => <div key={f.name} className={f.type === "textarea" || f.full ? "sm:col-span-2" : ""}><Field field={f} value={row[f.name]} onChange={(v) => update(i, { [f.name]: v })} compact /></div>)}
          </div>
          <ListControls i={i} len={list.length} onMove={move} onRemove={remove} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, {}])} className="btn-ghost text-xs py-2 min-h-0"><Plus size={14} /> {addLabel}</button>
    </div>
  );
}

export function TagsField({ value, onChange, placeholder }) {
  const [t, setT] = useState("");
  const list = Array.isArray(value) ? value : [];
  const add = () => { const v = t.trim(); if (!v) return; onChange([...list, v]); setT(""); };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">{list.map((x, i) => <span key={i} className="inline-flex items-center gap-1 text-xs bg-sage-100 text-forest px-2.5 py-1 rounded-full">{x}<button type="button" onClick={() => onChange(list.filter((_, k) => k !== i))} aria-label="Retirer" className="hover:text-red-700">×</button></span>)}</div>
      <div className="flex gap-2"><input className="input" value={t} placeholder={placeholder || "Ajouter puis Entrée"} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} /><button type="button" onClick={add} className="btn-ghost text-xs min-h-0 px-4"><Plus size={14} /></button></div>
    </div>
  );
}

export function EquipmentsField({ value, onChange }) {
  const v = value || {};
  return <div className="grid sm:grid-cols-2 gap-4">{EQUIP_CATEGORIES.map((c) => <div key={c}><p className="label">{c}</p><TagsField value={v[c]} onChange={(l) => onChange({ ...v, [c]: l })} placeholder="Ex. Lave-vaisselle" /></div>)}</div>;
}


const resolveOpts = (f, ctx) => (typeof f.options === "function" ? f.options(ctx) : f.options) || [];
const optVal = (o) => (typeof o === "string" ? o : o.value);
const optLab = (o) => (typeof o === "string" ? o : o.label);

function SelectInput({ f, id, value, onChange, tid, ctx }) {
  const opts = resolveOpts(f, ctx);
  return (
    <select id={id} className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      {f.allowEmpty && <option value="">—</option>}
      {opts.map((o) => <option key={optVal(o)} value={optVal(o)}>{optLab(o)}</option>)}
    </select>
  );
}

function MultiSelect({ f, value, onChange, ctx }) {
  const opts = resolveOpts(f, ctx);
  const list = value || [];
  const toggle = (val) => onChange(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);
  return (
    <div className="flex flex-wrap gap-2">
      {opts.map((o) => {
        const val = optVal(o);
        const on = list.includes(val);
        return <button type="button" key={val} onClick={() => toggle(val)} className={`px-3 py-1.5 rounded-full text-xs border ${on ? "bg-forest text-ecru border-forest" : "bg-white border-sand"}`}>{optLab(o)}</button>;
      })}
    </div>
  );
}

function SwitchInput({ f, value, onChange, tid }) {
  return (
    <label className="flex items-center gap-3 py-2 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 accent-forest" data-testid={tid} />
      <span className="text-sm text-forest">{f.label}</span>
      {f.help && <span className="text-xs text-charcoal/50">— {f.help}</span>}
    </label>
  );
}

function renderControl(f, { id, value, onChange, tid, ctx }) {
  const type = f.type || "text";
  if (type === "note") return <p className="text-xs text-charcoal/60 bg-cream rounded-xl p-3 whitespace-pre-line">{f.text}</p>;
  if (type === "textarea") return <textarea id={id} rows={f.rows || 4} className="input" value={value ?? ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  if (type === "number") return <input id={id} type="number" step={f.step || "any"} className="input" value={value ?? ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={tid} />;
  if (type === "select") return <SelectInput f={f} id={id} value={value} onChange={onChange} tid={tid} ctx={ctx} />;
  if (type === "multiselect") return <MultiSelect f={f} value={value} onChange={onChange} ctx={ctx} />;
  if (type === "image") return <ImageInput value={value} onChange={onChange} folder={f.folder} testId={tid} />;
  if (type === "images") {
    const onMain = f.withMain ? (url) => ctx.setForm?.({ ...ctx.form, main_image: url }) : null;
    return <ImagesField value={value} onChange={onChange} folder={f.folder} mainImage={ctx.form?.main_image} onMain={onMain} />;
  }
  if (type === "tags") return <TagsField value={value} onChange={onChange} placeholder={f.placeholder} />;
  if (type === "repeater") return <RepeaterField value={value} onChange={onChange} fields={f.fields} addLabel={f.addLabel} />;
  if (type === "equipments") return <EquipmentsField value={value} onChange={onChange} />;
  return <input id={id} type={type} className="input" value={value ?? ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
}

export function Field({ field: f, value, onChange, compact = false, ctx = {} }) {
  const id = `f-${f.name}`;
  const tid = f.testId || `field-${f.name}`;
  if (f.type === "switch") return <SwitchInput f={f} value={value} onChange={onChange} tid={tid} />;
  const showLabel = !compact || f.label;
  return (
    <div>
      {showLabel && <label htmlFor={id} className="label">{f.label}{f.required && " *"}</label>}
      {renderControl(f, { id, value, onChange, tid, ctx })}
      {f.help && !compact && <p className="text-xs text-charcoal/50 mt-1">{f.help}</p>}
    </div>
  );
}

